import styled from '@emotion/native';
import { Row } from 'components/base';
import {
  type ColumnConfig,
  type ColumnType,
  DataTable,
  FullPaddedRowWithBackgroundRow,
  type OnTableStateChangedHandler,
  type PositionProps,
  VerticalPaddedRow,
} from 'components/table';
import { CheckboxWithIcon, Spinner, Text } from 'design-system';
import { PADDING } from 'design-system/theme';
import { useLocalSearchParams, useRouter } from 'expo-router';
import type { BundleLink } from 'fhir/r4';
import {
  getNextTokenFromLink,
  type PageFetcher,
  usePaginatedData,
} from 'hooks/usePaginatedData';
import { useIsSmallerThan } from 'hooks/useResponsive';
import { useTableSelection } from 'hooks/useTableSelection';
import type React from 'react';
import { type ReactElement, useEffect, useMemo, useRef } from 'react';
import type { Column } from 'react-table';

import { TablePaginationControls } from './TablePaginationControls';

type RowType = 'padded-full-background' | 'padded-vertical';

interface WithBundleLink {
  link?: BundleLink[];
  nextToken?: string;
}

type CheckboxProps<TData extends {}> = {
  getRowId: (row: TData) => string;
  onSelectionChange: (ids: Set<string>) => void;
};

interface PaginatedTableProps<
  TData extends {},
  TResponse extends WithBundleLink,
> {
  cacheKey: any;
  columns: ColumnType<TData>[];
  columnConfig?: ColumnConfig<TData>;
  showHeader?: boolean;
  rowType?: RowType;
  /**
   * @deprecated striped tables are no longer supported in design system
   */
  isStriped?: boolean;
  disablePagination?: boolean;
  initialCount?: number;
  initialPage?: number;
  canChangeCount?: boolean;

  checkBox?: CheckboxProps<TData>;

  fetcher: PageFetcher<TResponse>;
  getDataFromResponse: (response: TResponse) => TData[];
  onTableStateChanged?: OnTableStateChangedHandler<TData>;
  onRowPress?: (data: TData) => void;
  onPageChange?: (page: number) => void;
}

const defaultColumn: Partial<Column> = {
  // @ts-ignore
  Header: ({ column }) => <Text>{column.id}</Text>,
  Cell: (props: { value: string }) => <Text>{props.value}</Text>,
  disableSortBy: true,
};

const MessageWrapper = styled(Row)({
  padding: PADDING.s,
});

const getNextToken = (data: WithBundleLink) => getNextTokenFromLink(data.link);

const rowTypes: Record<RowType, React.ComponentType<PositionProps>> = {
  'padded-full-background': FullPaddedRowWithBackgroundRow,
  'padded-vertical': VerticalPaddedRow,
};

const LoadingSpinner = () => (
  <MessageWrapper alignItems="center" justifyContent="center">
    <Spinner />
  </MessageWrapper>
);

export const PaginatedTable = <
  TData extends {},
  TResponse extends WithBundleLink,
>({
  initialPage = 1,
  cacheKey,
  fetcher,
  columns,
  showHeader,
  rowType = 'padded-full-background',
  getDataFromResponse,
  onTableStateChanged,
  onRowPress,
  onPageChange,
  initialCount = 20,
  canChangeCount = false,
  disablePagination = false,
  columnConfig,
  checkBox,
}: PaginatedTableProps<TData, TResponse>): ReactElement => {
  const isSmall = useIsSmallerThan('sm');
  const router = useRouter();
  const localParams = useLocalSearchParams();

  const {
    hasNext,
    error,
    hasPrev,
    data = [],
    isLoading,
    loadNext,
    setCount,
    loadPrev,
    resetPage,
    page,
  } = usePaginatedData(
    JSON.stringify(cacheKey),
    fetcher,
    getDataFromResponse,
    getNextToken,
    initialCount,
    initialPage,
  );

  const lastPageRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const pageParam = localParams.page;

    if (lastPageRef.current !== page) {
      lastPageRef.current = page;

      if (pageParam !== page.toString()) {
        router.setParams({ page: page.toString() });
      }

      onPageChange?.(page);
    }
  }, [localParams.page, onPageChange, page, router]);

  const tableData = data;
  const ResolvedRow = rowTypes[rowType];

  const selectionProps = useMemo(
    () => ({
      items: checkBox ? tableData : [],
      getItemId: checkBox ? checkBox.getRowId : () => '',
    }),
    [tableData, checkBox],
  );

  const {
    selection: selectedIds,
    onSelect,
    onSelectAll,
    isSelectedAll,
  } = useTableSelection(selectionProps);

  useEffect(() => {
    checkBox?.onSelectionChange?.(selectedIds);
  }, [selectedIds, checkBox]);

  const getRowCheckboxChecked = (row: TData) =>
    !!checkBox?.getRowId?.(row) && selectedIds.has(checkBox.getRowId(row));

  const onRowCheckboxChange = (row: TData) => {
    onSelect(row);
  };

  const getSelectAllCheckboxChecked = () => isSelectedAll;

  const onSelectAllCheckboxChange = () => {
    onSelectAll();
  };

  const checkboxColumn: ColumnType<TData> | null = checkBox
    ? {
        accessor: '__checkbox' as keyof TData,
        width: 40,
        header: (
          <CheckboxWithIcon
            checked={getSelectAllCheckboxChecked()}
            onChange={onSelectAllCheckboxChange}
            size={20}
          />
        ),
        disableSortBy: true,
        Cell: ({ row }: { row: { original: TData } }) => (
          <CheckboxWithIcon
            checked={getRowCheckboxChecked(row.original)}
            onChange={() => onRowCheckboxChange(row.original)}
            size={20}
          />
        ),
      }
    : null;

  const finalColumns = checkboxColumn ? [checkboxColumn, ...columns] : columns;

  if (error) {
    return (
      <MessageWrapper>
        <Text>Error</Text>
      </MessageWrapper>
    );
  }

  return (
    <>
      <DataTable<TData>
        Row={ResolvedRow}
        data={tableData}
        columns={finalColumns}
        defaultColumn={defaultColumn as Partial<Column<TData>>}
        showLoading={isLoading}
        LoadingSpinner={LoadingSpinner}
        showHeader={showHeader}
        onTableStateChanged={onTableStateChanged}
        onRowPress={onRowPress}
        useCards={isSmall}
        columnConfig={columnConfig}
      />

      <TablePaginationControls
        initialCount={initialCount}
        hasPages={!disablePagination}
        canChangeCount={canChangeCount}
        currentPage={page}
        canPressNext={hasNext}
        canPressPrevious={hasPrev}
        onNextPress={loadNext}
        onPreviousPress={loadPrev}
        onCountChange={setCount}
        resetPage={resetPage}
      />
    </>
  );
};
